// client/src/components/company/VatCreditDashboard.js
// Dashboard di dettaglio del prodotto Crediti IVA: gare dell'anno, importo assegnato, ultimo tasso ottenuto,
// gare attive e calendario delle allocazioni per periodo del credito.
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { formatCurrencyWithSymbol } from '../utils/formatUtils';
import { formatCreditTypeAndPeriod } from '../vatCredit/vatCreditUtils';
import { useResponsive } from '../../hooks/useResponsive';
import { TrendingUp, Gavel, Coins, Percent, CalendarDays, Info } from 'lucide-react';

const VAT_CREDIT_COLOR = '#1a73e8';
// Colonne del calendario: i 3 trimestri del Modello TR e la dichiarazione annuale
const CALENDAR_PERIODS = [
  { key: 'q1', quarter: 1 },
  { key: 'q2', quarter: 2 },
  { key: 'q3', quarter: 3 },
  { key: 'annual', quarter: null }
];

const kpiValueStyle = { fontSize: '26px', fontWeight: 'bold', color: VAT_CREDIT_COLOR, lineHeight: 1.2, margin: '6px 0 4px' };
const kpiNoteStyle = { fontSize: '13px', color: '#767e8e' };
const emptyStateStyle = { textAlign: 'center', padding: '24px', color: '#666' };

const VatCreditDashboard = () => {
  const { t } = useTranslation();
  // Passed to the detail page so its back button returns here
  const location = useLocation();
  const fromDashboard = { from: location.pathname };
  const { isMobile } = useResponsive();
  const { isAuthenticated, token, initialized } = useAuth();

  const [data, setData] = useState(null);
  const [openRequests, setOpenRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [calendarYear, setCalendarYear] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError('');
      try {
        if (token) {
          axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        }
        const [dashboardRes, requestsRes] = await Promise.all([
          axios.get('/api/vat-credit-requests/dashboard'),
          axios.get('/api/vat-credit-requests')
        ]);
        setData(dashboardRes.data);
        setOpenRequests(
          requestsRes.data.requests
            .filter(request => ['bidding', 'pending_allocation'].includes(request.status))
            .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
        );
      } catch (err) {
        console.error('VAT credit dashboard fetch error:', err);
        setError(t('common.errors.fetchDashboard', {
          defaultValue: 'Failed to fetch dashboard data. Please try again.'
        }));
      } finally {
        setLoading(false);
      }
    };

    if (isAuthenticated && initialized) {
      fetchData();
    }
  }, [isAuthenticated, token, initialized, t]);

  const getStatusTranslation = (status) => {
    switch (status) {
      case 'bidding':
        return t('status.bidding');
      case 'pending_allocation':
        return t('status.pending_allocation');
      default:
        return status.replace('_', ' ');
    }
  };

  const formatDate = (date) => new Date(date).toLocaleDateString();
  const currency = data?.currency || 'EUR';
  const year = data?.year || new Date().getFullYear();

  const renderKpiHeader = (Icon, label) => (
    <div className="card-title">
      <Icon className="card-title-icon" size={20} style={{ color: VAT_CREDIT_COLOR }} />
      <span style={{ color: VAT_CREDIT_COLOR }}>{label}</span>
    </div>
  );

  // ── KPI: gare dell'anno, allocate con successo e in corso ──
  const renderAuctionsCard = () => {
    const auctions = data?.auctions || { total: 0, allocated: 0, inProgress: 0 };
    return (
      <div className="card" style={{ marginBottom: 0 }}>
        {renderKpiHeader(Gavel, t('vatCreditDashboard.auctionsInYear', { year }))}
        <div style={kpiValueStyle}>{auctions.total}</div>
        <div style={kpiNoteStyle}>{t('vatCreditDashboard.allocatedSuccessfully', { count: auctions.allocated })}</div>
        <div style={{ ...kpiNoteStyle, marginTop: '4px' }}>
          {auctions.inProgress > 0
            ? t('vatCreditDashboard.inProgress', { count: auctions.inProgress })
            : t('vatCreditDashboard.noneInProgress')}
        </div>
      </div>
    );
  };

  // ── KPI: importo assegnato sulle gare dell'anno ──
  const renderAllocatedAmountCard = () => (
    <div className="card" style={{ marginBottom: 0 }}>
      {renderKpiHeader(Coins, t('vatCreditDashboard.allocatedAmountInYear', { year }))}
      <div style={kpiValueStyle}>
        {formatCurrencyWithSymbol(Math.round(data?.allocatedAmount || 0), currency)}
      </div>
      <div style={kpiNoteStyle}>{t('vatCreditDashboard.allocatedAmountNote', { year })}</div>
    </div>
  );

  // ── KPI: ultimo tasso ottenuto (variabili con l'Euribor alla data di allocazione) ──
  const renderLastRateCard = () => {
    const lastRate = data?.lastRate;
    return (
      <div className="card" style={{ marginBottom: 0 }}>
        {renderKpiHeader(Percent, t('vatCreditDashboard.lastRate'))}
        {lastRate ? (
          <>
            <div style={kpiValueStyle}>{`${lastRate.value.toFixed(2)}%`}</div>
            <div style={kpiNoteStyle}>
              {t('vatCreditDashboard.lastRateNote', { requestId: lastRate.requestId, date: formatDate(lastRate.allocatedAt) })}
            </div>
            {lastRate.baseRateValue != null && (
              <div style={{ ...kpiNoteStyle, fontSize: '11.5px', marginTop: '10px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                <Info size={12} />
                {t('vatCreditDashboard.lastRateFloatingInfo', { value: lastRate.baseRateValue.toFixed(3), date: formatDate(lastRate.baseRateDate) })}
              </div>
            )}
          </>
        ) : (
          <div style={{ ...kpiNoteStyle, marginTop: '10px' }}>{t('vatCreditDashboard.noAllocations', { year })}</div>
        )}
      </div>
    );
  };

  // ── Gare attive (bidding + pending_allocation) ──
  const renderOpenRequests = () => (
    <div className={`card ${isMobile ? 'mobile-card-view' : ''}`}>
      <div className="card-title">
        <TrendingUp className="card-title-icon" size={20} />
        <span>{t('vatCreditDashboard.activeAuctions')}</span>
      </div>

      {!isMobile && (
        <table className="requests-list-table">
          <thead>
            <tr>
              <th>{t('requests.requestId')}</th>
              <th>{t('vatCreditDashboard.credit')}</th>
              <th>{t('vatCredit.amount')}</th>
              <th>{t('bids.bidsReceived')}</th>
              <th>{t('vatCredit.bidDeadline')}</th>
              <th>{t('bids.status')}</th>
            </tr>
          </thead>
          <tbody>
            {openRequests.length > 0 ? (
              openRequests.map(request => (
                <tr key={request.id}>
                  <td>
                    <Link to={`/company/vat-credit/requests/${request.id}`} state={fromDashboard} className="view-btn">#{request.requestId}</Link>
                  </td>
                  <td>{formatCreditTypeAndPeriod(request, t) || '-'}</td>
                  <td>{formatCurrencyWithSymbol(request.amount, request.currency || 'EUR')}</td>
                  <td>{formatCurrencyWithSymbol(request.totalActiveBidAmount || 0, request.currency || 'EUR')}</td>
                  <td>{formatDate(request.bidDeadline)}</td>
                  <td>
                    <span className={`status status-${request.status}`}>{getStatusTranslation(request.status)}</span>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={6}>{t('vatCreditDashboard.noActiveAuctions')}</td>
              </tr>
            )}
          </tbody>
        </table>
      )}

      {isMobile && (
        <div className="mobile-cards-container">
          {openRequests.length > 0 ? (
            openRequests.map(request => (
              <div className="mobile-record-card" key={request.id}>
                <div className="mobile-card-header">
                  <div className="mobile-card-title">
                    <Link to={`/company/vat-credit/requests/${request.id}`} state={fromDashboard} className="view-btn">#{request.requestId}</Link>
                  </div>
                  <div className="mobile-card-status">
                    <span className={`status status-${request.status}`}>{getStatusTranslation(request.status)}</span>
                  </div>
                </div>
                <div className="mobile-card-body">
                  <div className="mobile-card-row">
                    <span className="mobile-card-label">{t('vatCreditDashboard.credit')}</span>
                    <span className="mobile-card-value">{formatCreditTypeAndPeriod(request, t) || '-'}</span>
                  </div>
                  <div className="mobile-card-row">
                    <span className="mobile-card-label">{t('vatCredit.amount')}</span>
                    <span className="mobile-card-value">{formatCurrencyWithSymbol(request.amount, request.currency || 'EUR')}</span>
                  </div>
                  <div className="mobile-card-row">
                    <span className="mobile-card-label">{t('bids.bidsReceived')}</span>
                    <span className="mobile-card-value">{formatCurrencyWithSymbol(request.totalActiveBidAmount || 0, request.currency || 'EUR')}</span>
                  </div>
                  <div className="mobile-card-row">
                    <span className="mobile-card-label">{t('vatCredit.bidDeadline')}</span>
                    <span className="mobile-card-value">{formatDate(request.bidDeadline)}</span>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="mobile-record-card mobile-empty-card">{t('vatCreditDashboard.noActiveAuctions')}</div>
          )}
        </div>
      )}
    </div>
  );

  // ── Calendario: cosa è stato allocato per ogni periodo del credito, a chi e per quale importo ──
  const renderCalendar = () => {
    const entries = data?.calendar || [];
    const years = [...new Set([year, ...entries.map(entry => entry.creditYear)])].sort((a, b) => b - a);
    const selectedYear = calendarYear && years.includes(calendarYear) ? calendarYear : year;
    const yearEntries = entries.filter(entry => entry.creditYear === selectedYear);

    return (
      <div className="card">
        <div className="card-title">
          <CalendarDays className="card-title-icon" size={20} />
          <span>{t('vatCreditDashboard.calendar')}</span>
          {years.length > 1 && (
            <select
              aria-label={t('vatCredit.creditYear')}
              value={selectedYear}
              onChange={(e) => setCalendarYear(Number(e.target.value))}
              style={{ width: 'auto', marginLeft: 'auto', padding: '4px 10px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '13px', fontWeight: 500, cursor: 'pointer', backgroundColor: '#fff' }}
            >
              {years.map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          )}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : `repeat(${CALENDAR_PERIODS.length}, minmax(0, 1fr))`, gap: '12px' }}>
          {CALENDAR_PERIODS.map(period => {
            const periodEntries = yearEntries.filter(entry => (period.quarter
              ? entry.creditType === 'tr_model' && entry.creditQuarter === period.quarter
              : entry.creditType === 'annual_declaration'));
            const total = periodEntries.reduce((sum, entry) => sum + entry.amount, 0);
            const isAllocated = periodEntries.length > 0;

            return (
              <div
                key={period.key}
                style={{
                  border: `1px solid ${isAllocated ? '#c9ddf8' : '#eef1f6'}`,
                  backgroundColor: isAllocated ? '#f5f9ff' : '#fafbfc',
                  borderRadius: '8px',
                  padding: '12px 14px',
                  minWidth: 0
                }}
              >
                <div style={{ fontSize: '11.5px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.7px', color: '#767e8e' }}>
                  {period.quarter
                    ? t('vatCredit.quarterOption', { n: period.quarter })
                    : t('vatCreditDashboard.annual')}
                </div>
                {isAllocated ? (
                  <>
                    <div style={{ fontSize: '18px', fontWeight: 700, color: VAT_CREDIT_COLOR, margin: '6px 0 10px' }}>
                      {formatCurrencyWithSymbol(Math.round(total), currency)}
                    </div>
                    <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                      {periodEntries.map((entry, index) => (
                        <li
                          key={`${entry.requestMongoId}-${entry.bankName}-${index}`}
                          style={{ padding: '6px 0', borderTop: '1px solid #e8eef7', fontSize: '13px' }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#1f2937' }} title={entry.bankName || ''}>
                              {entry.bankName || '-'}
                            </span>
                            <span style={{ fontWeight: 600, color: '#1f2937', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                              {formatCurrencyWithSymbol(Math.round(entry.amount), entry.currency)}
                            </span>
                          </div>
                          <div style={{ fontSize: '11.5px', color: '#767e8e', marginTop: '2px' }}>
                            <Link to={`/company/vat-credit/requests/${entry.requestMongoId}`} state={fromDashboard}>#{entry.requestId}</Link>
                            {entry.effectiveRate !== null && ` · ${entry.effectiveRate.toFixed(2)}%`}
                            {` · ${formatDate(entry.executionDate)}`}
                          </div>
                        </li>
                      ))}
                    </ul>
                  </>
                ) : (
                  <div style={{ fontSize: '13px', color: '#9aa1ad', marginTop: '6px' }}>{t('vatCreditDashboard.notAllocated')}</div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  if (loading) {
    return <div className="loading">{t('common.loadingDashboard')}</div>;
  }

  if (error) {
    return <div className="error-message">{error}</div>;
  }

  if (!data) {
    return <div style={emptyStateStyle}>{t('common.loadingDashboard')}</div>;
  }

  return (
    <div className="company-dashboard">
      {/* KPI: gare dell'anno, importo assegnato, ultimo tasso ottenuto */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, minmax(0, 1fr))', gap: '20px', marginBottom: '20px' }}>
        {renderAuctionsCard()}
        {renderAllocatedAmountCard()}
        {renderLastRateCard()}
      </div>

      {renderOpenRequests()}
      {renderCalendar()}
    </div>
  );
};

export default VatCreditDashboard;
